/**
 * ReportIssueButton — Bouton flottant "Signaler un problème" en bas à droite.
 * Remplace l'ancien VoiceAssistant dans AppShell.
 *
 * Le modal collecte : titre, description, catégorie, priorité, module (auto-détecté via URL).
 * Contexte automatique : URL, user_agent, résolution d'écran.
 */

import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bug, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { submitReport } from "@/lib/api";
import DictateButton from "@/components/DictateButton";

// Auto-détection du module à partir de l'URL
function detectModule(pathname) {
  if (pathname.includes("/advice")) return "advice";
  if (pathname.includes("/creator")) return "creator";
  if (pathname.includes("/contracts")) return "contracts";
  if (pathname.includes("/studio")) return "studio";
  if (pathname.includes("/legal-search")) return "legal_search";
  if (pathname.includes("/prediction")) return "prediction";
  if (pathname.includes("/agent")) return "agent";
  if (pathname.includes("/court-portal") || pathname.includes("/portal")) return "court_portal";
  if (pathname.includes("/negotiation")) return "negotiation";
  if (pathname.includes("/due-diligence") || pathname.includes("/dd")) return "due_diligence";
  if (pathname.includes("/compliance")) return "compliance";
  if (pathname.includes("/templates")) return "templates";
  if (pathname.includes("/calculators")) return "calculators";
  if (pathname.includes("/settings")) return "settings";
  if (pathname.includes("/knowledge")) return "knowledge_base";
  if (pathname === "/" || pathname.includes("/dashboard")) return "dashboard";
  if (pathname.includes("/admin")) return "admin";
  if (pathname.includes("/pdf")) return "pdf_lab";
  return "other";
}

export default function ReportIssueButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "bug",
    severity: "medium",
  });

  const handleOpen = () => {
    setOpen(true);
    setSubmitted(false);
    setForm({ title: "", description: "", category: "bug", severity: "medium" });
  };

  const handleSubmit = async () => {
    if (form.title.length < 3) {
      toast.error("Le titre doit contenir au moins 3 caractères");
      return;
    }
    if (form.description.length < 10) {
      toast.error("La description doit contenir au moins 10 caractères");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        ...form,
        module: detectModule(location.pathname),
        context: {
          url: window.location.href,
          pathname: location.pathname,
          screen: `${window.screen.width}x${window.screen.height}`,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          timestamp_local: new Date().toISOString(),
        },
      };
      const res = await submitReport(payload);
      setSubmitted(true);
      toast.success(res.data?.message || "Signalement reçu");
      setTimeout(() => setOpen(false), 2500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi du signalement");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Bouton flottant — coin inférieur droit */}
      <button
        onClick={handleOpen}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full bg-primary text-white shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center group"
        title="Signaler un problème ou une suggestion"
        aria-label="Signaler un problème"
      >
        <Bug className="h-6 w-6 group-hover:scale-110 transition-transform" />
      </button>

      {/* Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bug className="h-5 w-5 text-primary" />
              Signaler un problème
            </DialogTitle>
            <DialogDescription>
              Décrivez brièvement ce qui ne fonctionne pas ou ce que vous aimeriez voir ajouté.
              L'équipe recevra votre signalement et s'en occupera.
            </DialogDescription>
          </DialogHeader>

          {submitted ? (
            <div className="py-8 flex flex-col items-center gap-3 text-center">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
              <p className="text-lg font-medium">Signalement reçu !</p>
              <p className="text-sm text-muted-foreground">
                Merci. Vous pouvez suivre le statut dans « Mes signalements ».
              </p>
            </div>
          ) : (
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium mb-1 block">Titre *</label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Ex. Le créateur ne sauvegarde pas le document"
                  maxLength={200}
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-1 block">Description *</label>
                <div className="relative">
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Qu'avez-vous essayé de faire ? Que s'est-il passé ? Quelle erreur voyez-vous ? (🎤 dictée disponible)"
                    rows={5}
                    maxLength={5000}
                    className="pr-12"
                  />
                  <div className="absolute bottom-2 right-2">
                    <DictateButton
                      onTranscript={(t) => setForm((prev) => ({
                        ...prev,
                        description: prev.description + (prev.description && !prev.description.endsWith(" ") ? " " : "") + t,
                      }))}
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {form.description.length}/5000 caractères
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Catégorie</label>
                  <Select
                    value={form.category}
                    onValueChange={(v) => setForm({ ...form, category: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bug">Bogue</SelectItem>
                      <SelectItem value="feature_request">Demande de fonctionnalité</SelectItem>
                      <SelectItem value="performance">Performance</SelectItem>
                      <SelectItem value="ui_ux">Interface / UX</SelectItem>
                      <SelectItem value="data_issue">Problème de données</SelectItem>
                      <SelectItem value="login_issue">Connexion</SelectItem>
                      <SelectItem value="payment">Paiements</SelectItem>
                      <SelectItem value="other">Autre</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">Priorité</label>
                  <Select
                    value={form.severity}
                    onValueChange={(v) => setForm({ ...form, severity: v })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Faible</SelectItem>
                      <SelectItem value="medium">Moyen</SelectItem>
                      <SelectItem value="high">Élevé</SelectItem>
                      <SelectItem value="critical">Critique</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground">
                Nous joignons automatiquement : page actuelle ({detectModule(location.pathname)}), navigateur, résolution.
                Nous ne collectons ni mots de passe ni données de vos dossiers.
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
                  Annuler
                </Button>
                <Button onClick={handleSubmit} disabled={submitting}>
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    "Envoyer le signalement"
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
